// @flow
import * as React from "react";
import { Formik } from "formik";
import { navigate } from "gatsby";

import { t, _ } from "ttag";
import classnames from "classnames";
import { FormFeedback } from "reactstrap";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { v4 as uuidv4 } from "uuid";

import { Section } from "@containers";
import { FormInput, Icon, Error, EmailReceive } from "@2po-c21/components";

import { PROPERTY_TYPES } from "./propertyTypes";
import { validationSchema } from "./validationSchema";

import {
    createKeyActions,
    fetchSuggestions,
    submitForm,
    validatePostalCode,
} from "./helpers";
import styles from "./style.module.scss";

type Props = {
    agencyData?: Array<any>,
    agencyFallbackImage?: string,
    locale: string,
    country: string,
    estimationFormSuccessPage?: {
        slug: string,
    },
};

const EstimationForm = ({
    agencyData,
    locale,
    country,
    estimationFormSuccessPage,
}: Props) => {
    const accessToken = process.env.GATSBY_MAPBOX_TOKEN;
    const [sessionToken, setSessionToken] = React.useState("");

    const { executeRecaptcha } = useGoogleReCaptcha();

    const [formStatus, setFormStatus] = React.useState(null);
    const [selectedAgencyData, setSelectedAgencyData] = React.useState(null);

    const [suggestions, setSuggestions] = React.useState([]);
    const [dropdownIsOpen, setdropdownIsOpen] = React.useState(false);
    const [isClickingDropdown, setIsClickingDropdown] = React.useState(false);
    const [focusedSuggestionIndex, setFocusedSuggestionIndex] =
        React.useState(-1);

    const formRef = React.useRef(null);

    const handleAddressSelect = (setFieldValue, suggestion) => {
        setFieldValue("street", suggestion.name);
        setFieldValue("postalCode", suggestion.postalCode);
        setFieldValue("city", suggestion.city);
    };

    const scrollToTop = () => {
        formRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // session token for Mapbox
    React.useEffect(() => {
        setSessionToken(uuidv4());
    }, []);

    React.useEffect(() => {
        if (typeof window !== "undefined") {
            const searchParams = new URLSearchParams(window.location.search);
            const agencyId = searchParams.get("agency");

            if (agencyId) {
                const agency = agencyData.find(
                    (node) => node.api_id === agencyId,
                );
                setSelectedAgencyData(agency);
            }
        }
    }, [agencyData]);

    const updateDisplayedAgencyAfterSubmission = (responseData) => {
        const agency = agencyData.find(
            (agency) => agency.api_id === responseData.agencyId,
        );

        setSelectedAgencyData(agency);

        if (estimationFormSuccessPage?.slug) {
            const agencyApiId = agency?.api_id;

            const getRedirectUrl = (slug, agencyApiId) => {
                return `${slug}?agency_api_id=${agencyApiId}`;
            };

            const redirectUrl = getRedirectUrl(
                estimationFormSuccessPage.slug,
                agencyApiId,
            );

            navigate(redirectUrl);
            setFormStatus("success");
        }
    };

    return (
        <>
            <Section theme="themeSecondary">
                <div className={styles.estimationFormContainer} ref={formRef}>
                    <h2 className={styles.formTitle}>
                        {t`form.estimation.title`}
                    </h2>
                    <Formik
                        initialValues={{
                            propertyType: "",
                            firstName: "",
                            lastName: "",
                            email: "",
                            phoneNumber: "",
                            postalCode: "",
                            street: "",
                            number: "",
                            city: "",
                            extraInfo: "",
                            gdpr: false,
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (
                            values,
                            { resetForm, setFieldError },
                        ) => {
                            try {
                                await submitForm(
                                    values,
                                    locale,
                                    executeRecaptcha,
                                    setFormStatus,
                                    resetForm,
                                    selectedAgencyData
                                        ? selectedAgencyData.api_id
                                        : null,
                                    updateDisplayedAgencyAfterSubmission,
                                    scrollToTop,
                                );
                            } catch (error) {
                                if (error.message === "Invalid postal code") {
                                    setFieldError(
                                        "postalCode",
                                        t`form.estimation.error.invalid-postal-code`,
                                    );
                                } else {
                                    setFormStatus("error");
                                }
                            }
                        }}
                    >
                        {({
                            values,
                            handleChange,
                            handleSubmit,
                            touched,
                            errors,
                            handleBlur,
                            isSubmitting,
                            setFieldValue,
                            setFieldError,
                        }) => {
                            const handleStreetInputChange = async (e) => {
                                handleChange(e);
                                await fetchSuggestions(
                                    e.target.value,
                                    setSuggestions,
                                    accessToken,
                                    sessionToken,
                                    country,
                                    locale,
                                    setdropdownIsOpen,
                                );
                            };

                            const handleStreetBlur = (e) => {
                                if (!isClickingDropdown) {
                                    handleBlur(e);
                                    setSuggestions([]);
                                }
                            };

                            const handlePostalCodeBlur = async (e) => {
                                handleBlur(e);

                                const isValidPostalCode =
                                    await validatePostalCode(e.target.value);
                                if (!isValidPostalCode) {
                                    setFieldError(
                                        "postalCode",
                                        t`form.estimation.error.invalid-postal-code`,
                                    );
                                }
                            };

                            const keyActions = createKeyActions(
                                suggestions,
                                focusedSuggestionIndex,
                                setFocusedSuggestionIndex,
                                handleAddressSelect,
                                setFieldValue,
                                setdropdownIsOpen,
                            );

                            const handleStreetKeyDown = (event) => {
                                if (!dropdownIsOpen || suggestions.length === 0)
                                    return;
                                keyActions[event.key]?.(event);
                            };

                            return (
                                <form
                                    onSubmit={handleSubmit}
                                    className={classnames(styles.form)}
                                >
                                    <fieldset
                                        className={classnames(
                                            styles.fieldsetGrid,
                                        )}
                                    >
                                        <legend
                                            className={styles.hidden}
                                        >{t`form.estimation.property-details.legend`}</legend>
                                        <div
                                            className={classnames(
                                                styles.formGroup,
                                                styles.fullWidth,
                                                styles.propertyTypeWrapper,
                                            )}
                                        >
                                            <label
                                                htmlFor="propertyType"
                                                className={styles.hidden}
                                            >{t`form.estimation.property-type.label`}</label>
                                            <select
                                                id="propertyType"
                                                name="propertyType"
                                                value={values.propertyType}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                aria-invalid={
                                                    touched.propertyType &&
                                                    errors.propertyType
                                                }
                                                className={classnames(
                                                    styles.goldBackground,
                                                    touched.propertyType &&
                                                        errors.propertyType &&
                                                        styles.error,
                                                    touched.propertyType &&
                                                        errors.propertyType &&
                                                        "is-invalid",
                                                )}
                                            >
                                                <option value="">{t`form.estimation.property-type.label`}</option>
                                                {PROPERTY_TYPES.map((type) => (
                                                    <option
                                                        key={type.value}
                                                        value={type.value}
                                                    >
                                                        {_(type.labelKey)}
                                                    </option>
                                                ))}
                                            </select>
                                            {touched.propertyType &&
                                                errors.propertyType && (
                                                    <div>
                                                        <Icon
                                                            variant="red"
                                                            icon={Error}
                                                            size="sm"
                                                        />
                                                        <FormFeedback
                                                            className={
                                                                styles.errorText
                                                            }
                                                        >
                                                            {_(
                                                                errors.propertyType,
                                                            )}
                                                        </FormFeedback>
                                                    </div>
                                                )}
                                        </div>
                                    </fieldset>

                                    <fieldset
                                        className={classnames(
                                            styles.fieldsetGrid,
                                        )}
                                    >
                                        <legend
                                            className={styles.hidden}
                                        >{t`form.estimation.contact-details.legend`}</legend>
                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.firstname`}
                                                placeholder={t`form.estimation.contact.firstname`}
                                                name="firstName"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.firstName}
                                                invalid={
                                                    touched.firstName &&
                                                    errors.firstName
                                                }
                                                errorMessage={
                                                    touched.firstName &&
                                                    errors.firstName
                                                        ? _(errors.firstName)
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.lastname`}
                                                placeholder={t`form.estimation.contact.lastname`}
                                                name="lastName"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.lastName}
                                                invalid={
                                                    touched.lastName &&
                                                    errors.lastName
                                                }
                                                errorMessage={
                                                    touched.lastName &&
                                                    errors.lastName
                                                        ? _(errors.lastName)
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.email`}
                                                placeholder={t`form.estimation.contact.email`}
                                                name="email"
                                                type="email"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.email}
                                                invalid={
                                                    touched.email &&
                                                    errors.email
                                                }
                                                errorMessage={
                                                    touched.email &&
                                                    errors.email
                                                        ? _(errors.email)
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.telephone`}
                                                placeholder={t`form.estimation.contact.telephone`}
                                                name="phoneNumber"
                                                type="tel"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.phoneNumber}
                                                invalid={
                                                    touched.phoneNumber &&
                                                    errors.phoneNumber
                                                }
                                                errorMessage={
                                                    touched.phoneNumber &&
                                                    errors.phoneNumber
                                                        ? _(errors.phoneNumber)
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.street`}
                                                placeholder={t`form.estimation.contact.street`}
                                                name="street"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={
                                                    handleStreetInputChange
                                                }
                                                onBlur={handleStreetBlur}
                                                value={values.street}
                                                invalid={
                                                    touched.street &&
                                                    errors.street
                                                }
                                                errorMessage={
                                                    touched.street &&
                                                    errors.street
                                                        ? _(errors.street)
                                                        : ""
                                                }
                                                autoComplete="off"
                                                onKeyDown={handleStreetKeyDown}
                                                aria-expanded={dropdownIsOpen}
                                                aria-controls="address-suggestions"
                                                aria-autocomplete="list"
                                                role="combobox"
                                            />
                                            {suggestions.length > 0 &&
                                                dropdownIsOpen && (
                                                    <div
                                                        id="address-suggestions"
                                                        className={
                                                            styles.suggestionsDropdown
                                                        }
                                                        onMouseDown={() =>
                                                            setIsClickingDropdown(
                                                                true,
                                                            )
                                                        }
                                                        onMouseUp={() =>
                                                            setIsClickingDropdown(
                                                                false,
                                                            )
                                                        }
                                                        role="listbox"
                                                    >
                                                        {suggestions.map(
                                                            (
                                                                suggestion,
                                                                index,
                                                            ) => (
                                                                <div
                                                                    key={
                                                                        suggestion.mapbox_id
                                                                    }
                                                                    role="option"
                                                                    aria-selected={
                                                                        index ===
                                                                        focusedSuggestionIndex
                                                                    }
                                                                    onClick={() => {
                                                                        setdropdownIsOpen(
                                                                            false,
                                                                        );
                                                                        handleAddressSelect(
                                                                            setFieldValue,
                                                                            suggestion,
                                                                        );
                                                                    }}
                                                                    onMouseEnter={() =>
                                                                        setFocusedSuggestionIndex(
                                                                            index,
                                                                        )
                                                                    }
                                                                    className={classnames(
                                                                        styles.suggestionItem,
                                                                        index ===
                                                                            focusedSuggestionIndex &&
                                                                            styles.suggestionItemFocused,
                                                                    )}
                                                                >
                                                                    {
                                                                        suggestion.name
                                                                    }
                                                                    ,{" "}
                                                                    {
                                                                        suggestion.postalCode
                                                                    }{" "}
                                                                    {suggestion.locality ||
                                                                        suggestion.city}
                                                                </div>
                                                            ),
                                                        )}
                                                    </div>
                                                )}
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.number`}
                                                placeholder={t`form.estimation.contact.number`}
                                                name="number"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.number}
                                                invalid={
                                                    touched.number &&
                                                    errors.number
                                                }
                                                errorMessage={
                                                    touched.number &&
                                                    errors.number
                                                        ? _(errors.number)
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.postal-code`}
                                                placeholder={t`form.estimation.contact.postal-code`}
                                                name="postalCode"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handlePostalCodeBlur}
                                                value={values.postalCode}
                                                invalid={
                                                    touched.postalCode &&
                                                    errors.postalCode
                                                }
                                                errorMessage={
                                                    touched.postalCode &&
                                                    errors.postalCode
                                                        ? _(errors.postalCode)
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div className={styles.inputWrapper}>
                                            <FormInput
                                                label={t`form.estimation.contact.city`}
                                                placeholder={t`form.estimation.contact.city`}
                                                name="city"
                                                required
                                                background="white"
                                                border="none"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                value={values.city}
                                                invalid={
                                                    touched.city && errors.city
                                                }
                                                errorMessage={
                                                    touched.city && errors.city
                                                        ? _(errors.city)
                                                        : ""
                                                }
                                            />
                                        </div>
                                    </fieldset>

                                    <fieldset
                                        className={classnames(
                                            styles.fieldsetGrid,
                                        )}
                                    >
                                        <legend
                                            className={styles.hidden}
                                        >{t`form.estimation.additional-info.legend`}</legend>
                                        <div
                                            className={classnames(
                                                styles.formGroup,
                                                styles.fullWidth,
                                            )}
                                        >
                                            <FormInput
                                                label={t`form.estimation.contact.extra-info`}
                                                placeholder={t`form.estimation.contact.extra-info`}
                                                name="extraInfo"
                                                background="white"
                                                border="none"
                                                type="textarea"
                                                rows={5}
                                                value={values.extraInfo}
                                                onChange={handleChange}
                                            />
                                        </div>
                                    </fieldset>

                                    <div className={styles.formGroup}>
                                        <div
                                            className={classnames(
                                                styles.privacyCheckbox,
                                                touched.gdpr &&
                                                    errors.gdpr &&
                                                    "is-invalid",
                                            )}
                                        >
                                            <input
                                                type="checkbox"
                                                id="gdpr"
                                                name="gdpr"
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                checked={values.gdpr}
                                                aria-invalid={
                                                    touched.gdpr && errors.gdpr
                                                }
                                                className={classnames(
                                                    styles.checkbox,
                                                )}
                                            />
                                            <label htmlFor="gdpr">
                                                {t`form.estimation.gdpr.privacy-message`}{" "}
                                                <a
                                                    href={`/${locale}/privacy-policy`}
                                                >
                                                    {t`form.estimation.gdpr.privacy-policy`}
                                                </a>
                                                {". "}
                                                {t`form.estimation.gdpr.recaptcha-warning`}{" "}
                                                <a href="https://policies.google.com/privacy">
                                                    {t`form.estimation.gdpr.recaptcha-policy`}
                                                </a>{" "}
                                                {t`form.estimation.gdpr.and`}{" "}
                                                <a href="https://policies.google.com/terms">
                                                    {t`form.estimation.gdpr.recaptcha-tos`}
                                                </a>
                                                {"."}
                                            </label>
                                        </div>
                                        {touched.gdpr && errors.gdpr && (
                                            <FormFeedback>
                                                <Icon
                                                    variant="red"
                                                    icon={Error}
                                                    size="sm"
                                                />{" "}
                                                {_(errors.gdpr)}
                                            </FormFeedback>
                                        )}
                                    </div>
                                    {formStatus === "error" && (
                                        <div className={styles.errorMessage}>
                                            {t`form.estimation.error.submission`}
                                        </div>
                                    )}
                                    <div
                                        className={classnames(
                                            styles.submitButton,
                                            !(touched.gdpr && errors.gdpr) &&
                                                styles.extraMargin,
                                        )}
                                    >
                                        <button
                                            type="submit"
                                            className={styles.button}
                                            disabled={isSubmitting}
                                        >
                                            <Icon
                                                icon={EmailReceive}
                                                size={"sm"}
                                                variant={"relentless-gold"}
                                                className={styles.submitIcon}
                                            />
                                            {t`form.estimation.submit`}
                                        </button>
                                    </div>
                                </form>
                            );
                        }}
                    </Formik>
                </div>
            </Section>
        </>
    );
};

export default EstimationForm;
